import { Code, ConnectError } from "@connectrpc/connect";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Paper from "@mui/material/Paper";
import Rating from "@mui/material/Rating";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ErrorStateView } from "@viajero/shared-ui/error-boundary";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryStateFallback } from "@/components/QueryStateFallback";
import { useGetNPSContext } from "@/features/nps/api/useGetNPSContext";
import { useSubmitNPSAnswer } from "@/features/nps/api/useSubmitNPSAnswer";
import { NPSThanksView } from "@/features/nps/components/NPSThanksView";
import { formatGuestName } from "@/lib/formatGuestName";
import { formatReservationDateTime } from "@/lib/formatReservationDateTime";
import { colors } from "@/theme/colors";

// 自由記入欄の最大文字数。crud.nps.comment は TEXT で Backend 側に制限が無いため、
// 過大な入力を入り口で抑えるためのフロント側の上限 (AllergyRequestSection と同方式)。
const COMMENT_MAX_LENGTH = 500;

/**
 * NPS アンケート画面 (/nps/{npsToken})。来店確定後のアンケート依頼メール
 * ({GUEST_BASE_URL}/nps/{npsToken}) から開き、星の数 (1〜5。必須) と自由記入欄 (任意) を
 * SubmitNPSAnswer で保存する (§6.7)。表示・認可はすべて npsToken (NPSTokenLayout が
 * interceptor へ渡す) に基づく。
 *
 * 評価対象は店舗単体ではなく「コンシェルジュの案内・予約から利用まで」を含む本サービスの体験全体
 * (質問文の通り)。そのため予約情報は「どの来店についての質問か」を思い出すための最小限
 * (来店日時・店舗名・ゲスト名) に絞り、GetNPSContext もこの最小限の項目だけを返す
 * (JPTP_CANSEI-1570 / JPTP_CANSEI-1584)。
 */
export function NPSSurveyStep() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useGetNPSContext();
  const submitAnswer = useSubmitNPSAnswer();

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // 言語切替に追従できるよう、state には翻訳済み文字列ではなく i18n キーを保持する。
  const [submitErrorKey, setSubmitErrorKey] = useState<string | null>(null);

  const reservation = data?.reservation;
  // 期限切れ (発行 + 7 日) は Interceptor が FailedPrecondition で返すため、URL 不正 (NotFound)
  // とは別の案内を出す (§6.7)。
  const isExpired = error instanceof ConnectError && error.code === Code.FailedPrecondition;
  const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;

  if (isExpired) {
    return (
      <ErrorStateView
        severity="warning"
        title={t("nps.expired")}
        description={t("nps.expiredDescription")}
      />
    );
  }

  if (!reservation) {
    return (
      <QueryStateFallback
        isLoading={isLoading}
        // 取得は成功したが reservation が空のケースも「見つからない」として扱う
        // (CancelConfirmStep と同じ考え方)。
        isNotFound={isNotFound || (!isLoading && !isError)}
        isError={isError}
        notFound={{
          title: t("nps.notFound"),
          description: t("nps.notFoundDescription"),
        }}
        error={{
          title: t("nps.error"),
          description: t("nps.errorDescription"),
          actions: [{ label: t("errorPage.reload"), onClick: () => void refetch() }],
        }}
      />
    );
  }

  if (submitted) {
    return <NPSThanksView title={t("nps.thanksTitle")} message={t("nps.thanksMessage")} />;
  }

  // 1 予約 = 1 回答。回答済みのトークンで再訪した場合はフォームを出さない (§6.7)。
  if (data?.answered) {
    return <NPSThanksView title={t("nps.answeredTitle")} message={t("nps.answeredMessage")} />;
  }

  const guestName = formatGuestName(
    reservation.guestLastName,
    reservation.guestFirstName,
    i18n.language,
  );
  const formattedDateTime = formatReservationDateTime(
    reservation.visitDate,
    reservation.visitTime,
    i18n.language,
  );

  const handleSubmit = async () => {
    if (rating === null) return;
    setSubmitErrorKey(null);
    try {
      await submitAnswer.mutateAsync({ rating, comment });
      setSubmitted(true);
    } catch (e) {
      // 別タブ等で先に回答されていた場合 (FailedPrecondition) は最新の回答済み状態を取り直し、
      // 回答済み表示へ切り替える。取り直しに失敗したときのために案内も出す。
      if (e instanceof ConnectError && e.code === Code.FailedPrecondition) {
        void refetch();
        setSubmitErrorKey("nps.alreadyAnsweredError");
        return;
      }
      setSubmitErrorKey("nps.submitError");
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 4, maxWidth: 640, width: "100%", mx: "auto" }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: colors.text, textAlign: "center", pb: 1 }}
      >
        {t("nps.title")}
      </Typography>
      <Typography sx={{ color: colors.textSub, textAlign: "center", pb: 4 }}>
        {t("nps.subtitle")}
      </Typography>

      {guestName && (
        <Typography sx={{ color: colors.textBody, mb: 2 }}>
          {t("nps.greeting", { name: guestName })}
        </Typography>
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: colors.border,
          px: 3,
          py: 3,
          textAlign: "center",
        }}
      >
        <Typography sx={{ color: colors.textMuted, fontSize: 14, mb: 1 }}>
          {t("nps.visitLabel")}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.text, mb: 1 }}>
          {formattedDateTime}
        </Typography>
        <Typography sx={{ color: colors.textSub }}>{reservation.shopName}</Typography>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* 質問文を fieldset の legend にして星のラジオ群と関連付ける (支援技術は各星の読み上げ時に
          legend を併せて読む)。必須は FormControl の required ではなくラベル内のテキストで伝える
          — MUI が描くアスタリスクは aria-hidden のため支援技術に届かない
          (キャンセル理由の「（必須）」と同じ流儀)。 */}
      <FormControl component="fieldset" fullWidth sx={{ m: 0, p: 0, border: 0 }}>
        <FormLabel
          component="legend"
          sx={{
            color: colors.textBody,
            fontWeight: 700,
            mb: 2,
            "&.Mui-focused": { color: colors.textBody },
          }}
        >
          {t("nps.question")}
          <Box component="span" sx={{ ml: 0.5, color: colors.textSub, fontWeight: 400 }}>
            {t("nps.requiredLabel")}
          </Box>
        </FormLabel>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Rating
            name="npsRating"
            value={rating}
            onChange={(_, value) => setRating(value)}
            getLabelText={(value) => t("nps.ratingValueLabel", { rating: value })}
            sx={{ fontSize: 44, "& .MuiRating-iconFilled": { color: colors.gold } }}
          />
        </Box>
      </FormControl>

      <TextField
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        multiline
        minRows={6}
        fullWidth
        placeholder={t("nps.commentPlaceholder")}
        aria-label={t("nps.commentPlaceholder")}
        slotProps={{ htmlInput: { maxLength: COMMENT_MAX_LENGTH } }}
      />

      {submitErrorKey && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {t(submitErrorKey)}
        </Alert>
      )}

      <Button
        variant="contained"
        color="secondary"
        fullWidth
        disabled={rating === null || submitAnswer.isPending}
        onClick={() => void handleSubmit()}
        // 送信中もラベルは差し替えず、装飾のスピナーを添えてアクセシブルネームを保つ
        // (スピナーは aria-hidden で読み上げ対象から外す)。
        startIcon={
          submitAnswer.isPending ? (
            <CircularProgress size={20} color="inherit" aria-hidden="true" />
          ) : undefined
        }
        sx={{ mt: 4, minHeight: 48 }}
      >
        {t("nps.submit")}
      </Button>
    </Box>
  );
}
